/**
 * SITE CONTEXT
 * Provides site-specific configuration throughout the app
 * Enables single codebase to power multiple domains
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import { getSiteConfig, SITE_CONFIGS, DOCTOR_INFO, THEMES } from '../config/siteConfig';

const SiteContext = createContext(null);

export const SiteProvider = ({ children, forceSite = null }) => {
  const [siteConfig, setSiteConfig] = useState(() => {
    if (forceSite && SITE_CONFIGS[forceSite]) {
      return SITE_CONFIGS[forceSite];
    }
    return getSiteConfig();
  });

  // Apply theme CSS variables when site changes
  useEffect(() => {
    const theme = siteConfig.theme;
    const root = document.documentElement;
    
    // Update CSS custom properties
    root.style.setProperty('--primary', theme.primary);
    root.style.setProperty('--accent', theme.accent);
    root.style.setProperty('--ring', theme.primary);
    
    // Update document title
    document.title = `${siteConfig.title} | ${DOCTOR_INFO.name}`;
    
    // Update meta description
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', siteConfig.metaDescription);
    }
    
    // Update theme color for mobile browsers
    const themeColor = document.querySelector('meta[name="theme-color"]');
    if (themeColor) {
      themeColor.setAttribute('content', theme.primaryHex);
    }
  }, [siteConfig]);

  // Function to switch sites (useful for preview/development)
  const switchSite = (siteId) => {
    if (SITE_CONFIGS[siteId]) {
      setSiteConfig(SITE_CONFIGS[siteId]);
    }
  };

  // Get theme utility classes
  const getThemeClasses = () => {
    const theme = siteConfig.theme;
    return {
      text: theme.textClass,
      bg: theme.bgClass,
      border: theme.borderClass,
      hover: theme.hoverBg,
      lightBg: theme.lightBg,
      gradient: theme.gradient,
      bgGradient: theme.bgGradient
    };
  };

  // Check if current site is the main hub
  const isMainSite = () => siteConfig.id === 'main';

  // Get related site configs
  const getRelatedSites = () => {
    return siteConfig.relatedSites?.map(siteId => SITE_CONFIGS[siteId]) || [];
  };

  const value = {
    ...siteConfig,
    siteId: siteConfig.id,
    doctor: DOCTOR_INFO,
    theme: siteConfig.theme,
    allSites: SITE_CONFIGS,
    switchSite,
    getThemeClasses,
    isMainSite,
    getRelatedSites
  };

  return (
    <SiteContext.Provider value={value}>
      {children}
    </SiteContext.Provider>
  );
};

// Hook to use site context
export const useSite = () => {
  const context = useContext(SiteContext);
  if (!context) {
    throw new Error('useSite must be used within a SiteProvider');
  }
  return context;
};

// Hook to get theme classes
export const useTheme = () => {
  const { theme, getThemeClasses } = useSite();
  return { theme, classes: getThemeClasses() };
};

export default SiteContext;
